// Thin API client for the Go backend (apps/api).
//
// In production the admin is served statically by Nginx, which reverse-proxies
// /api -> the Go service, so the base is empty and we hit same-origin /api/v1.
// Override with VITE_API_BASE for split-origin dev (e.g. http://localhost:8080).

import { clearLogin, getAuthToken, replaceAuthToken } from "@/lib/auth";

const BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
const PREFIX = "/api/v1";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function url(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${BASE}${PREFIX}${p}`;
}

// Connectivity reporting for the global degraded banner. Lazy import breaks
// the api ⇄ apiHealth module cycle.
type Health = { markApiOk: () => void; markApiFail: () => void };
let health: Health | null = null;
import("./apiHealth").then((m) => { health = m; }).catch(() => {});
function reportOutcome(res: Response | null): void {
  if (!health) return;
  if (res && res.status < 502) health.markApiOk(); // any live answer incl. 4xx/500
  else health.markApiFail(); // network reject or gateway 502/503/504
}

function authHeaders(headers: HeadersInit = {}): Headers {
  const result = new Headers(headers);
  const token = getAuthToken();
  if (token) result.set("Authorization", `Bearer ${token}`);
  return result;
}

async function trackedFetch(input: string, init?: RequestInit): Promise<Response> {
  try {
    const res = await fetch(input, init);
    reportOutcome(res);
    if (res.status === 401) {
      clearLogin();
      window.location.replace("/");
    } else {
      const token = res.headers.get("X-Auth-Token");
      if (token) replaceAuthToken(token);
    }
    return res;
  } catch (e) {
    if (!(init?.signal as AbortSignal | undefined)?.aborted) reportOutcome(null);
    throw e;
  }
}
// apiFetch exposes the authenticated, response-level client for callers that
// need to consume a blob or construct multipart FormData themselves.
export function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  return trackedFetch(apiUrl(path), {
    ...init,
    headers: authHeaders(init?.headers),
  });
}


export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  // no-store: 编辑保存后 reload() 必须拿到最新数据,默认 HTTP 缓存会回旧值(需硬刷新才变)。
  const res = await trackedFetch(url(path), { signal, headers: authHeaders({ Accept: "application/json" }), cache: "no-store" });
  if (!res.ok) throw new ApiError(res.status, `GET ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

export async function apiSend<T>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  options?: { headers?: HeadersInit; signal?: AbortSignal },
): Promise<T> {
  const res = await trackedFetch(url(path), {
    method,
    signal: options?.signal,
    headers: authHeaders({ ...Object.fromEntries(new Headers(options?.headers)), "Content-Type": "application/json", Accept: "application/json" }),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new ApiError(res.status, msg || `${method} ${path} failed: ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// apiUpload posts a single file as multipart/form-data and returns parsed JSON.
// extras 用于带额外的 text 字段（如 title / description / tags）。field 默认 "file"。
export async function apiUpload<T>(
  path: string,
  file: File,
  field: string | { field?: string; extras?: Record<string, string> } = "file",
  extras?: Record<string, string>,
): Promise<T> {
  // 兼容旧签名 apiUpload(path, file, "file") 与新签名 apiUpload(path, file, { extras }).
  let fieldName = "file";
  let extraFields: Record<string, string> | undefined = extras;
  if (typeof field === "string") {
    fieldName = field;
  } else if (field && typeof field === "object") {
    fieldName = field.field ?? "file";
    extraFields = field.extras ?? extras;
  }
  const fd = new FormData();
  fd.append(fieldName, file);
  if (extraFields) {
    for (const [k, v] of Object.entries(extraFields)) fd.append(k, v);
  }
  const res = await trackedFetch(url(path), { method: "POST", body: fd, headers: authHeaders({ Accept: "application/json" }) });
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new ApiError(res.status, msg || `upload ${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

// apiUrl builds an absolute backend URL (for <audio>/<img> sources and blobs).
export function apiUrl(path: string): string {
  return url(path);
}
